import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { useTheme } from './theme';
import LangSwitcher from './LangSwitcher';

const API_URL = process.env.REACT_APP_API_URL ?? '';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  // 🔐 2FA ikkinchi bosqichi (docs/knowledge/148): parol to'g'ri → server «challenge» beradi.
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  const goNext = (data) => {
    login(data.token, data.user);
    toast.success(`Xush kelibsiz, ${data.user.username}!`);
    if (data.usedRecovery) toast('🔑 Zaxira kod ishlatildi — 👤 → 🔐 Ikki bosqichli kirish bo\'limida yangilarini oling', { duration: 8000 });
    // 🔐 Majburiy 2FA hali sozlanmagan — avval sozlash sahifasi.
    if (data.mfaSetupRequired) { navigate('/2fa-setup'); return; }
    // 👥 Taklif havolasidan kelgan bo'lsa — o'sha yerga qaytamiz (faqat /invite/ — ochiq redirect emas).
    const next = new URLSearchParams(window.location.search).get('next') || '';
    navigate(/^\/invite\/[\w-]+$/.test(next) ? next : '/');
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error || "Kod noto'g'ri");
        if (data.code === 'CHALLENGE_EXPIRED') { setChallenge(null); setCode(''); }
        return;
      }
      goNext(data);
    } catch {
      toast.error('Server bilan bog\'lanib bo\'lmadi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error || 'Login failed');
        return;
      }
      if (data.twoFactorRequired) { setChallenge(data.challenge); setCode(''); setUseRecovery(false); return; }
      goNext(data);
    } catch {
      toast.error('Server bilan bog\'lanib bo\'lmadi');
    } finally {
      setLoading(false);
    }
  };

  // Flowbite "Sign in" sahifasi: kulrang fon, markazda oq karta, logotip tepada.
  const inputCls = 'bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 focus:ring-2 focus:outline-none block w-full p-2.5';
  const [theme, toggleTheme] = useTheme();
  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen px-6 pt-8 pb-8 bg-gray-50 dark:bg-gray-900">
      <div className="absolute top-4 right-4 flex items-center gap-1">
        {/* 🌐 til — kirishdan oldin ham (standart English) */}
        <LangSwitcher up={false} />
        <button type="button" onClick={() => toggleTheme()} title={theme === 'dark' ? 'Kunduzgi rejim' : 'Tungi rejim'}
          className="p-2 text-lg leading-none text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
      </div>
      <div className="flex items-center justify-center mb-8 text-2xl font-semibold text-gray-900 dark:text-white">
        <img src="/logo192.png" alt="" className="kh-brand-logo w-9 h-9 mr-3 rounded-lg object-cover shadow-sm" />
        <span>Khorezmai</span>
      </div>
      <div className="w-full max-w-md p-6 space-y-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm sm:p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{challenge ? '🔐 Tasdiqlash kodi' : 'Tizimga kirish'}</h2>
        {challenge ? (
          <form onSubmit={handleCode} className="space-y-5">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {useRecovery
                ? "Saqlab qo'ygan zaxira kodlaringizdan birini kiriting (masalan: abcde-fghjk)."
                : 'Telefoningizdagi autentifikator ilovasi (Google Authenticator, Authy…) ko\'rsatgan 6 xonali kodni kiriting.'}
            </p>
            <input
              value={code}
              onChange={e => setCode(e.target.value)}
              className={`${inputCls} ${useRecovery ? 'font-mono' : 'text-center text-xl tracking-[0.4em] font-mono'}`}
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              placeholder={useRecovery ? 'abcde-fghjk' : '123456'}
              maxLength={useRecovery ? 16 : 6}
              required
              autoFocus
            />
            <button type="submit" disabled={loading} className="w-full px-5 py-3 text-base font-medium text-center text-white rounded-lg bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 focus:ring-4 focus:ring-primary-300 disabled:opacity-60 disabled:cursor-not-allowed transition">
              {loading ? 'Tekshirilmoqda...' : 'Tasdiqlash'}
            </button>
            <div className="flex justify-between text-sm">
              <button type="button" onClick={() => { setUseRecovery(v => !v); setCode(''); }} className="text-primary-700 dark:text-primary-400 hover:underline">
                {useRecovery ? '6 xonali kod bilan' : 'Zaxira kod bilan kirish'}
              </button>
              <button type="button" onClick={() => { setChallenge(null); setCode(''); }} className="text-gray-500 hover:underline">← Orqaga</button>
            </div>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Foydalanuvchi nomi</label>
            <input
              type="text"
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              className={inputCls}
              placeholder="admin"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Parol</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className={inputCls}
              placeholder="••••••••"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 text-base font-medium text-center text-white rounded-lg bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 focus:ring-4 focus:ring-primary-300 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Kirish...' : 'Kirish'}
          </button>
        </form>
        )}
      </div>
      <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">Khorezmai — AI dasturchi studiya</p>
    </div>
  );
}
